/**
 * SB6-ANA-LIST-02 — defect-list table with sortable headers + density toggle.
 *
 * Replaces the freeform `<ListItemButton>` rows in `DefectsPanel` with a
 * compact table whose columns + sort directions are locked in
 * `.vibe/12_STORY_BOOK_6_FIXES.md` §C1:
 *
 *   | Name | Group | Linked | Severity | Check |
 *
 *   * Name — `Defect.catalogueLabel` (BE pre-resolves i18n `name` JSONB
 *     to `en` with fallback to `de`). Sort A→Z.
 *   * Group — defect grouping feature not implemented yet → em-dash
 *     placeholder; column visible per spec so the future feature has a
 *     home. Sort by integer (count-in-group), 0 today.
 *   * Linked — defect-linking feature partially implemented; count
 *     surfaced via `Defect.links?.length`. Sort by that count.
 *   * Severity — `Defect.severity` label + `Defect.classCategory`
 *     integer for sort. Column HIDDEN ENTIRELY when no class-model is
 *     active (per OQ-SB6-C5).
 *   * Check — checkbox indicator for "viewed by current user via
 *     `image_review_states`". Sort: viewed first (desc).
 *
 * The density toggle (Compact / Normal / Large) lives in the panel's
 * header row; `useDefectDensity` persists the choice per user.
 *
 * The component is intentionally renderless of any data fetching — the
 * parent `DefectsPanel` owns the query + edit-permission gating and
 * passes items + handlers down.
 */
import { useMemo, useState } from "react";
import {
  Box,
  Checkbox,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import { StatusPill } from "@aeriosiq/ui";
import type { Defect } from "@aeriosiq/types";
import { statusToStatusPill } from "./queries.js";
import { DefectGradeChip, defectGradeText } from "./DefectGradeChip.js";
import { defectSeveritySortRank } from "./severityDisplay.js";
import {
  denyReasonTooltip,
  type DefectEditPermission,
} from "../analysis/editPermission.js";
import { DefectLinkIcon } from "./DefectLinkIcon.js";
import type { DefectDensity } from "./useDefectDensity.js";
import { DEFECT_DENSITY_ROW_HEIGHT } from "./useDefectDensity.js";

const EM_DASH = "—";

type SortKey = "name" | "group" | "linked" | "severity" | "check";
type SortDir = "asc" | "desc";

interface DefectsTableProps {
  items: readonly Defect[];
  selectedId: string | null;
  onRowClick: (defect: Defect) => void;
  onRowContextMenu: (
    e: React.MouseEvent<HTMLElement>,
    defectId: string,
  ) => void;
  onOpenStatusEditor: (
    e: React.MouseEvent<HTMLElement>,
    defectId: string,
  ) => void;
  onOpenSeverityEditor: (
    e: React.MouseEvent<HTMLElement>,
    defectId: string,
  ) => void;
  inspectionId: string;
  editPermission?: DefectEditPermission;
  /**
   * When `false`, the Severity column is hidden entirely (per
   * OQ-SB6-C5 — severity has no meaning without a class model). True
   * by default so existing direct-mount paths keep the column.
   */
  showSeverityColumn?: boolean;
  /**
   * Set of image ids the current user has reviewed at least once. The
   * Check column reads from this set per `defect.imageId`. When omitted
   * every row renders unchecked.
   */
  viewedImageIds?: ReadonlySet<string>;
  density: DefectDensity;
}

function sortDefects(
  items: readonly Defect[],
  sortBy: SortKey,
  sortDir: SortDir,
  viewedImageIds: ReadonlySet<string>,
): Defect[] {
  const dir = sortDir === "asc" ? 1 : -1;
  const copy = [...items];
  copy.sort((a, b) => {
    // Severity story (8.0.0 / UX Q6) — ungraded (null) sorts LAST on the
    // severity column regardless of direction, so the not-graded majority
    // never floats above graded rows.
    if (sortBy === "severity") {
      const aR = defectSeveritySortRank(a);
      const bR = defectSeveritySortRank(b);
      if (aR == null || bR == null) {
        if (aR == null && bR == null) {
          return a.catalogueLabel.localeCompare(b.catalogueLabel) * dir;
        }
        return aR == null ? 1 : -1;
      }
    }
    let cmp = 0;
    switch (sortBy) {
      case "name": {
        cmp = a.catalogueLabel.localeCompare(b.catalogueLabel);
        break;
      }
      case "group": {
        // No group data on the wire yet — defer to name so order is
        // deterministic across renders.
        cmp = a.catalogueLabel.localeCompare(b.catalogueLabel);
        break;
      }
      case "linked": {
        // `links` is a non-optional contract field, but unit-test
        // fixtures pre-1.6.0 omit it. Safe-access keeps the table
        // sortable in those harnesses.
        const aN = (a as { links?: readonly unknown[] }).links?.length ?? 0;
        const bN = (b as { links?: readonly unknown[] }).links?.length ?? 0;
        cmp = aN - bN;
        if (cmp === 0) cmp = a.catalogueLabel.localeCompare(b.catalogueLabel);
        break;
      }
      case "severity": {
        // Nulls are handled (direction-independent) above; here both rows
        // are graded.
        const aN = defectSeveritySortRank(a) ?? 0;
        const bN = defectSeveritySortRank(b) ?? 0;
        cmp = aN - bN;
        if (cmp === 0) cmp = a.catalogueLabel.localeCompare(b.catalogueLabel);
        break;
      }
      case "check": {
        const aV = viewedImageIds.has(a.imageId) ? 1 : 0;
        const bV = viewedImageIds.has(b.imageId) ? 1 : 0;
        cmp = aV - bV;
        if (cmp === 0) cmp = a.catalogueLabel.localeCompare(b.catalogueLabel);
        break;
      }
    }
    return cmp * dir;
  });
  return copy;
}

export function DefectsTable({
  items,
  selectedId,
  onRowClick,
  onRowContextMenu,
  onOpenStatusEditor,
  onOpenSeverityEditor,
  inspectionId,
  editPermission,
  showSeverityColumn = true,
  viewedImageIds,
  density,
}: DefectsTableProps): React.ReactElement {
  const [sortBy, setSortBy] = useState<SortKey>("name");
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  const canEdit = editPermission?.allowed !== false;
  const denyTooltip =
    editPermission && !editPermission.allowed
      ? denyReasonTooltip(editPermission.reason)
      : null;

  const viewedSet = useMemo<ReadonlySet<string>>(
    () => viewedImageIds ?? new Set<string>(),
    [viewedImageIds],
  );

  const sorted = useMemo(
    () => sortDefects(items, sortBy, sortDir, viewedSet),
    [items, sortBy, sortDir, viewedSet],
  );

  const handleSort = (key: SortKey, defaultDir: SortDir): void => {
    if (sortBy !== key) {
      setSortBy(key);
      setSortDir(defaultDir);
      return;
    }
    setSortDir(sortDir === "asc" ? "desc" : "asc");
  };

  const rowHeight = DEFECT_DENSITY_ROW_HEIGHT[density];
  const isCompact = density === "compact";
  // Padding shrinks at compact density so 32-px rows fit the controls
  // without clipping; large density keeps the standard inset.
  const cellPaddingY = isCompact ? 0.25 : density === "normal" ? 0.75 : 1.25;
  const cellPaddingX = 1.25;

  return (
    <TableContainer
      sx={{
        // Inherit the panel's scroll behaviour; this matches the legacy
        // non-virtualised render path.
        overflowY: "auto",
        // Compact density needs the table headers to stick so the analyst
        // doesn't lose context while scrolling a 700-row list.
        "& thead th": {
          position: "sticky",
          top: 0,
          zIndex: 1,
          bgcolor: "background.paper",
        },
      }}
    >
      <Table size="small" aria-label="Defects table">
        <TableHead>
          <TableRow>
            <SortableHeader
              label="Name"
              active={sortBy === "name"}
              dir={sortDir}
              onClick={() => {
                handleSort("name", "asc");
              }}
            />
            <SortableHeader
              label="Group"
              active={sortBy === "group"}
              dir={sortDir}
              onClick={() => {
                handleSort("group", "asc");
              }}
              align="right"
              width={64}
            />
            <SortableHeader
              label="Linked"
              active={sortBy === "linked"}
              dir={sortDir}
              onClick={() => {
                handleSort("linked", "asc");
              }}
              align="right"
              width={72}
            />
            {showSeverityColumn ? (
              <SortableHeader
                label="Severity"
                active={sortBy === "severity"}
                dir={sortDir}
                onClick={() => {
                  handleSort("severity", "asc");
                }}
                width={108}
              />
            ) : null}
            <SortableHeader
              label="Check"
              active={sortBy === "check"}
              dir={sortDir}
              onClick={() => {
                handleSort("check", "desc");
              }}
              align="center"
              width={64}
            />
            {/* Status pill stays on the row but has no header label — it's
                a clickable affordance, not a sortable column. A spacer cell
                keeps the alignment honest. */}
            <TableCell padding="none" sx={{ width: 96 }} aria-hidden="true" />
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map((d) => {
            const linkedCount =
              (d as { links?: readonly unknown[] }).links?.length ?? 0;
            // SB6-ANA-LIST-02 — defect grouping isn't implemented yet;
            // keep the column visible (per spec) and render an em-dash
            // for every row until BR-NNN lands.
            const isViewed = viewedSet.has(d.imageId);
            const isSelected = selectedId === d.id;
            return (
              <TableRow
                key={d.id}
                hover
                selected={isSelected}
                data-defect-id={d.id}
                onClick={() => {
                  onRowClick(d);
                }}
                onContextMenu={(e) => {
                  e.preventDefault();
                  onRowContextMenu(e, d.id);
                }}
                sx={{
                  cursor: "pointer",
                  height: rowHeight,
                  "& td": {
                    py: cellPaddingY,
                    px: cellPaddingX,
                    fontSize: isCompact ? 12 : 13,
                  },
                }}
              >
                <TableCell>
                  <Stack
                    direction="row"
                    spacing={0.5}
                    alignItems="center"
                    sx={{ minWidth: 0 }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={d.catalogueLabel}
                    >
                      {d.catalogueLabel}
                    </Typography>
                    {d.originFindingId ? (
                      <Tooltip
                        title={`Promoted from finding ${d.originFindingId}`}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          aria-label="Promoted from finding"
                          sx={{ flexShrink: 0 }}
                        >
                          (from finding)
                        </Typography>
                      </Tooltip>
                    ) : null}
                  </Stack>
                </TableCell>
                <TableCell align="right">{EM_DASH}</TableCell>
                <TableCell align="right">
                  <Stack
                    direction="row"
                    spacing={0.5}
                    alignItems="center"
                    justifyContent="flex-end"
                  >
                    {linkedCount > 0 ? (
                      <>
                        <DefectLinkIcon
                          defectId={d.id}
                          inspectionId={inspectionId}
                          {...(editPermission ? { editPermission } : {})}
                        />
                        <span>{linkedCount}</span>
                      </>
                    ) : (
                      EM_DASH
                    )}
                  </Stack>
                </TableCell>
                {showSeverityColumn ? (
                  <TableCell>
                    <Tooltip
                      title={denyTooltip ?? ""}
                      disableHoverListener={canEdit}
                      disableFocusListener={canEdit}
                      disableTouchListener={canEdit}
                    >
                      <Box component="span" sx={{ display: "inline-flex" }}>
                        <Box
                          component="button"
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!canEdit) return;
                            onOpenSeverityEditor(e, d.id);
                          }}
                          disabled={!canEdit}
                          aria-disabled={!canEdit}
                          aria-label={`Change severity (currently ${defectGradeText(d)})`}
                          data-editor="severity"
                          data-defect-id={d.id}
                          sx={{
                            background: "none",
                            border: "none",
                            p: 0,
                            cursor: canEdit ? "pointer" : "not-allowed",
                            opacity: canEdit ? 1 : 0.5,
                          }}
                        >
                          {/* UX Q6 — class-model grade vocabulary; null →
                              "Not graded", never a fabricated grade. */}
                          <DefectGradeChip defect={d} dense />
                        </Box>
                      </Box>
                    </Tooltip>
                  </TableCell>
                ) : null}
                <TableCell align="center" padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={isViewed}
                    disabled
                    inputProps={{
                      "aria-label": isViewed
                        ? "Image viewed by you"
                        : "Image not yet viewed",
                    }}
                  />
                </TableCell>
                <TableCell align="right" padding="none" sx={{ pr: 1 }}>
                  <Tooltip
                    title={denyTooltip ?? ""}
                    disableHoverListener={canEdit}
                    disableFocusListener={canEdit}
                    disableTouchListener={canEdit}
                  >
                    <Box component="span" sx={{ display: "inline-flex" }}>
                      <Box
                        component="button"
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!canEdit) return;
                          onOpenStatusEditor(e, d.id);
                        }}
                        disabled={!canEdit}
                        aria-disabled={!canEdit}
                        aria-label={`Change status (currently ${d.status})`}
                        data-editor="status"
                        data-defect-id={d.id}
                        sx={{
                          background: "none",
                          border: "none",
                          p: 0,
                          cursor: canEdit ? "pointer" : "not-allowed",
                          opacity: canEdit ? 1 : 0.5,
                        }}
                      >
                        <StatusPill
                          status={statusToStatusPill(d.status)}
                          label={d.status}
                          dense
                        />
                      </Box>
                    </Box>
                  </Tooltip>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

interface SortableHeaderProps {
  label: string;
  active: boolean;
  dir: SortDir;
  onClick: () => void;
  align?: "left" | "right" | "center";
  width?: number;
}

function SortableHeader({
  label,
  active,
  dir,
  onClick,
  align = "left",
  width,
}: SortableHeaderProps): React.ReactElement {
  return (
    <TableCell
      align={align}
      sx={{
        ...(width ? { width } : {}),
        fontSize: 12,
        textTransform: "uppercase",
        letterSpacing: 0.4,
        fontWeight: 600,
        color: "text.secondary",
      }}
    >
      <TableSortLabel
        active={active}
        direction={active ? dir : "asc"}
        onClick={onClick}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );
}
